/**
 * Cálculos puros de nota e frequência da feature **Configuração de Nota e Frequência**.
 *
 * Toda a matemática do protótipo (`compute()`, `itemGrade`, `examsOf`) vive aqui, como
 * funções puras 100% testáveis. Não dependem de estado React nem de i18n.
 */

import { EXAMS, STAGES, TYPE_CONFIG } from '../mocks/mockGradeConfig';
import {
  type Activity,
  type ClassGroup,
  EActivityType,
  type GradeRefItem,
} from '../types/gradeConfig';

/** Mapa de notas/presenças simuladas, indexado pela chave da unidade de avaliação. */
export type SimInputs = Readonly<Record<string, string | number>>;

/** Unidade de avaliação (prova) resolvida a partir de uma atividade. */
export interface ExamUnit {
  /** Chave da nota (id da prova). */
  readonly key: string;
  /** Peso da prova dentro da disciplina (0–100). */
  readonly weight: number;
  /** Nome da prova (disciplina) ou `null` (a UI usa o rótulo do tipo). */
  readonly name: string | null;
  readonly lessonName: string | null;
  readonly lessonId: string | null;
  readonly date: string | null;
  /** Entra no cálculo da média. */
  readonly counts: boolean;
  /** Entra no cálculo da frequência. */
  readonly attendance: boolean;
  readonly attendanceByView: boolean;
  /** Chave da presença. */
  readonly attendanceKey: string;
}

/** Resultado de {@link itemGrade}. */
export interface ItemGrade {
  /** Média ponderada do item (0–10) ou `null` quando nenhuma nota foi preenchida. */
  readonly grade: number | null;
  /** Quantidade de provas com nota preenchida. */
  readonly filled: number;
  /** Total de provas que contam para a média. */
  readonly total: number;
}

/** Estatísticas agregadas por módulo, produzidas por {@link computeGradeAndAttendance}. */
export interface ModuleStats {
  readonly items: number;
  readonly gradedItems: number;
  readonly examsFilled: number;
  readonly examsTotal: number;
}

/** Resultado de {@link computeGradeAndAttendance}. */
export interface GradeComputation {
  readonly perModule: Record<string, ModuleStats>;
  readonly moduleGrade: Record<string, number | null>;
  /** Média final (média simples das médias dos módulos), ou `null` (parcial/vazia). */
  readonly finalGrade: number | null;
  /** Quantidade de módulos com atividades que contam para o cálculo. */
  readonly modulesCount: number;
  /** Quantidade de módulos com atividades que contam e que já têm média. */
  readonly modulesFilled: number;
  /** Frequência geral média (0–100), ou `null`. */
  readonly overallAttendance: number | null;
  readonly attendanceFilled: number;
  readonly attendanceTotal: number;
  readonly minGrade: number;
  readonly minAttendance: number;
}

/** Opções de mínimos de aprovação. */
export interface ComputeOptions {
  /** Nota mínima de aprovação (0–10). Default `7`. */
  readonly minGrade?: number;
  /** Frequência mínima de suficiência (0–100). Default `75`. */
  readonly minAttendance?: number;
}

/** Retorna a coleção de itens selecionáveis para o tipo de uma atividade. */
export function getSource(classGroup: ClassGroup, type: EActivityType): readonly GradeRefItem[] {
  const cfg = TYPE_CONFIG[type];
  if (cfg.source === 'fixedOptions') return cfg.fixedOptions ?? [];
  if (cfg.source === 'stages') return STAGES;
  if (cfg.source === 'exams') return EXAMS;
  return classGroup.disciplines;
}

/** Resolve o item de origem referenciado por uma atividade (ou `undefined`). */
export function sourceItem(classGroup: ClassGroup, at: Activity): GradeRefItem | undefined {
  return getSource(classGroup, at.type).find(x => x.id === at.refId);
}

/**
 * Expande uma atividade em suas unidades de avaliação (provas).
 *
 * - Disciplina com >1 aula: uma unidade por aula, com o peso da prova.
 * - Disciplina com 1 aula: a prova representa 100%.
 * - Demais tipos: uma única unidade de 100%.
 */
export function examsOf(at: Activity, classGroup: ClassGroup): ExamUnit[] {
  if (at.type === EActivityType.Discipline) {
    const d = sourceItem(classGroup, at);
    if (!d?.lessons) return [];
    const multi = d.lessons.length > 1;
    return d.lessons.map(l =>
      multi
        ? {
            key: l.exam.id,
            weight: Number(l.exam.weight) || 0,
            name: l.exam.name,
            lessonName: l.name,
            lessonId: l.id,
            date: l.date,
            counts: at.countsInGrade,
            attendance: at.countsInAttendance,
            attendanceByView: !!at.attendanceByView,
            attendanceKey: l.id,
          }
        : {
            key: l.exam.id,
            weight: 100,
            name: l.exam.name,
            lessonName: l.name,
            lessonId: l.id,
            date: l.date,
            counts: at.countsInGrade,
            attendance: at.countsInAttendance,
            attendanceByView: !!at.attendanceByView,
            attendanceKey: at.id,
          },
    );
  }
  return [
    {
      key: at.id,
      weight: 100,
      name: null,
      lessonName: null,
      lessonId: null,
      date: null,
      counts: at.countsInGrade,
      attendance: at.countsInAttendance,
      attendanceByView: !!at.attendanceByView,
      attendanceKey: at.id,
    },
  ];
}

/** Lê uma nota crua (0–10) do mapa de simulação, fixando na faixa; vazio → `null`. */
function readGrade(grades: SimInputs, key: string): number | null {
  const raw = String(grades[key] ?? '').trim();
  if (raw === '') return null;
  return Math.min(10, Math.max(0, Number(raw) || 0));
}

/** Média ponderada de uma atividade a partir das notas simuladas. */
export function itemGrade(at: Activity, classGroup: ClassGroup, grades: SimInputs): ItemGrade {
  const units = examsOf(at, classGroup).filter(p => p.counts);
  let sp = 0;
  let sw = 0;
  let filled = 0;
  for (const p of units) {
    const n = readGrade(grades, p.key);
    if (n !== null) {
      sp += n * p.weight;
      sw += p.weight;
      filled++;
    }
  }
  return { grade: sw > 0 ? sp / sw : null, filled, total: units.length };
}

/**
 * Calcula a média final e a frequência geral da turma a partir das notas/presenças
 * simuladas, respeitando os pesos de prova e o peso da atividade dentro do módulo. A média
 * final é a média simples das médias dos módulos (módulos não têm peso).
 */
export function computeGradeAndAttendance(
  classGroup: ClassGroup,
  grades: SimInputs,
  attendance: SimInputs,
  options?: ComputeOptions,
): GradeComputation {
  const minGrade = options?.minGrade ?? 7;
  const minAttendance = options?.minAttendance ?? 75;

  const perModule: Record<string, ModuleStats> = {};
  const acc: Record<
    string,
    {
      sp: number;
      sw: number;
      gradedItems: number;
      items: number;
      examsFilled: number;
      examsTotal: number;
    }
  > = {};

  for (const a of classGroup.activities) {
    const g = itemGrade(a, classGroup, grades);
    if (g.total === 0) continue;
    const mid = a.moduleId ?? 'none';
    const x = acc[mid] ?? {
      sp: 0,
      sw: 0,
      gradedItems: 0,
      items: 0,
      examsFilled: 0,
      examsTotal: 0,
    };
    acc[mid] = x;
    x.items++;
    x.examsTotal += g.total;
    x.examsFilled += g.filled;
    if (g.grade !== null) {
      const w = Number(a.moduleWeight) || 0;
      x.sp += g.grade * w;
      x.sw += w;
      x.gradedItems++;
    }
  }

  const moduleGrade: Record<string, number | null> = {};
  for (const mid of Object.keys(acc)) {
    const x = acc[mid];
    if (!x) continue;
    perModule[mid] = {
      items: x.items,
      gradedItems: x.gradedItems,
      examsFilled: x.examsFilled,
      examsTotal: x.examsTotal,
    };
    moduleGrade[mid] = x.sw > 0 ? x.sp / x.sw : null;
  }

  // Módulos não têm peso: a média final é a média simples (não ponderada) das médias
  // dos módulos que possuem atividades que contam para o cálculo.
  let fSP = 0;
  let fSW = 0;
  let modulesCount = 0;
  let modulesFilled = 0;
  for (const m of classGroup.modules) {
    const md = moduleGrade[m.id];
    if (md === undefined) continue;
    modulesCount++;
    if (md === null) continue;
    modulesFilled++;
    fSP += md;
    fSW += 1;
  }
  const finalGrade = fSW > 0 ? fSP / fSW : null;

  let attSum = 0;
  let attendanceFilled = 0;
  let attendanceTotal = 0;
  for (const a of classGroup.activities) {
    for (const p of examsOf(a, classGroup)) {
      if (!p.attendance) continue;
      attendanceTotal++;
      const raw = String(attendance[p.attendanceKey] ?? '').trim();
      if (raw !== '') {
        attSum += Math.min(100, Math.max(0, Number(raw) || 0));
        attendanceFilled++;
      }
    }
  }
  const overallAttendance = attendanceFilled > 0 ? attSum / attendanceFilled : null;

  return {
    perModule,
    moduleGrade,
    finalGrade,
    modulesCount,
    modulesFilled,
    overallAttendance,
    attendanceFilled,
    attendanceTotal,
    minGrade,
    minAttendance,
  };
}
